import * as Sentry from "@sentry/react";

/**
 * Custom logger utilities for structured logging with Sentry integration
 */

export type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

export interface LogContext {
  [key: string]: any;
}

/**
 * Log an error to Sentry with context
 */
export const logError = (
  error: Error | string,
  context?: LogContext,
  level: Sentry.SeverityLevel = 'error'
) => {
  // Add context as extras
  if (context) {
    Sentry.setContext('error_context', context);
  }

  // Capture the error
  if (error instanceof Error) {
    Sentry.captureException(error, {
      level,
      contexts: context ? { custom: context } : undefined,
    });
  } else {
    Sentry.captureMessage(error, {
      level,
      contexts: context ? { custom: context } : undefined,
    });
  }

  // Also log to console in development
  if (import.meta.env.MODE === 'development') {
    console.error('[Logger]', error, context);
  }
};

/**
 * Log an informational message to Sentry
 */
export const logInfo = (message: string, data?: LogContext) => {
  Sentry.captureMessage(message, {
    level: 'info',
    contexts: data ? { custom: data } : undefined,
  });

  if (import.meta.env.MODE === 'development') {
    console.log('[Logger]', message, data);
  }
};

/**
 * Log a warning to Sentry
 */
export const logWarning = (message: string, data?: LogContext) => {
  Sentry.captureMessage(message, {
    level: 'warning',
    contexts: data ? { custom: data } : undefined,
  });

  if (import.meta.env.MODE === 'development') {
    console.warn('[Logger]', message, data);
  }
};

/**
 * Add a breadcrumb for debugging context
 */
export const addBreadcrumb = (
  message: string,
  data?: Record<string, any>,
  level: Sentry.SeverityLevel = 'info',
  category?: string
) => {
  Sentry.addBreadcrumb({
    message,
    level,
    category: category || 'custom',
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Measure and log performance of an operation
 */
export const measurePerformance = async <T>(
  name: string,
  operation: () => Promise<T>,
  context?: LogContext
): Promise<T> => {
  const startTime = performance.now();
  
  // Start a Sentry transaction
  const transaction = Sentry.startInactiveSpan({
    name,
    op: 'custom',
  });

  try {
    addBreadcrumb(`Starting: ${name}`, context, 'info', 'performance');
    
    const result = await operation();
    
    const duration = performance.now() - startTime;
    
    addBreadcrumb(
      `Completed: ${name}`,
      { ...context, duration_ms: duration.toFixed(2) },
      'info',
      'performance'
    );

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    logError(
      error instanceof Error ? error : new Error(String(error)),
      { ...context, duration_ms: duration.toFixed(2), operation: name },
      'error'
    );
    
    throw error;
  } finally {
    transaction?.end();
  }
};

/**
 * Capture user feedback
 */
export const captureUserFeedback = (
  name?: string,
  email?: string,
  comments?: string
) => {
  const eventId = Sentry.lastEventId();
  
  if (!eventId) {
    logWarning('No event ID found for user feedback');
    return;
  }

  Sentry.captureFeedback({
    name: name || 'Anonymous',
    email: email || '',
    message: comments || '',
    associatedEventId: eventId,
  });

  logInfo('User feedback captured', {
    event_id: eventId,
    has_name: !!name,
    has_email: !!email,
  });
};

/**
 * Wrap a function with error tracking
 */
export const withErrorTracking = <T extends (...args: any[]) => any>(
  fn: T,
  context?: LogContext
): T => {
  return ((...args: Parameters<T>) => {
    try {
      const result = fn(...args);
      
      // Handle promises
      if (result instanceof Promise) {
        return result.catch((error) => {
          logError(error, { ...context, args }, 'error');
          throw error;
        });
      }
      
      return result;
    } catch (error) {
      logError(
        error instanceof Error ? error : new Error(String(error)),
        { ...context, args },
        'error'
      );
      throw error;
    }
  }) as T;
};

/**
 * Log database operation
 */
export const logDatabaseOperation = (
  operation: string,
  table: string,
  success: boolean,
  error?: Error,
  context?: LogContext
) => {
  addBreadcrumb(
    `Database ${operation} on ${table}`,
    {
      operation,
      table,
      success,
      error: error?.message,
      ...context,
    },
    success ? 'info' : 'error',
    'database'
  );

  if (!success && error) {
    logError(error, {
      operation,
      table,
      ...context,
    });
  }
};

/**
 * Log authentication event
 */
export const logAuthEvent = (
  event: 'login' | 'logout' | 'signup' | 'anonymous' | 'error',
  userId?: string,
  error?: Error,
  context?: LogContext
) => {
  addBreadcrumb(
    `Auth: ${event}`,
    {
      event,
      user_id: userId,
      error: error?.message,
      ...context,
    },
    error ? 'error' : 'info',
    'auth'
  );

  if (error) {
    logError(error, {
      auth_event: event,
      user_id: userId,
      ...context,
    });
  }
};

/**
 * Log API call
 */
export const logApiCall = (
  endpoint: string,
  method: string,
  status: number,
  duration?: number,
  error?: Error,
  context?: LogContext
) => {
  const success = status >= 200 && status < 300;
  
  addBreadcrumb(
    `API ${method} ${endpoint}`,
    {
      endpoint,
      method,
      status,
      duration_ms: duration,
      success,
      error: error?.message,
      ...context,
    },
    success ? 'info' : 'error',
    'http'
  );

  if (!success && error) {
    logError(error, {
      endpoint,
      method,
      status,
      duration_ms: duration,
      ...context,
    });
  }
};
